import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { encodeArrangement, isRoomRouteCredential, RoomColor, INITIAL_POSITIONS } from "./room-route-codec";

type Color = RoomColor;
type Phase = "playing" | "advancing" | "revealing" | "room" | "finished";
type Arrangement = Readonly<Record<Color, number>>;

interface PointerGesture {
  pointerId: number;
  color: Color;
  source: number;
  x: number;
  y: number;
  moved: boolean;
}

const COLORS: readonly Color[] = ["R", "Y", "B", "G"];
const START: Arrangement = INITIAL_POSITIONS;
const LEVELS: readonly Arrangement[] = [
  Object.freeze({ R: 0, Y: 3, B: 12, G: 15 }),
  Object.freeze({ R: 3, Y: 15, B: 0, G: 12 }),
  Object.freeze({ R: 15, Y: 12, B: 3, G: 0 }),
];
const COLOR_NAMES: Readonly<Record<Color, string>> = { R: "红", Y: "黄", B: "蓝", G: "绿" };
const COLOR_VARIABLES: Readonly<Record<Color, string>> = {
  R: "var(--red)", Y: "var(--yellow)", B: "var(--blue)", G: "var(--green)",
};
const GLYPHS: Readonly<Record<Color, string>> = {
  R: "m12 4 8 15H4Z",
  Y: "M12 5a7 7 0 1 0 0 14a7 7 0 1 0 0-14Z",
  B: "m12 3 9 9-9 9-9-9Z",
  G: "M5 5h14v14H5Z",
};

@Component({
  selector: "vbango-room-game",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./component.html",
  styles: [":host {\n  color-scheme: light;\n  --canvas: #f7f8fa;\n  --paper: #ffffff;\n  --ink: #26384c;\n  --muted: #697b90;\n  --accent: #185ba8;\n  --line: #dce3ec;\n  --red: #d76363;\n  --yellow: #e9b641;\n  --blue: #3c7dc5;\n  --green: #329778;\n  --cell-radius: 12px;\n  --board-size: max(216px, min(436px, calc(100vw - 80px), calc(100dvh - 238px)));\n  position: fixed;\n  inset: 0;\n  z-index: 500;\n  display: block;\n  visibility: visible;\n  overflow: auto;\n  background: var(--canvas);\n  color: var(--ink);\n  font-family: \"Microsoft YaHei\", \"PingFang SC\", sans-serif;\n  scrollbar-color: #b9c9dc var(--canvas);\n  scrollbar-width: thin;\n}\n\n/* The public game owns this anonymous surface; its underlying controls must\n   not enter keyboard navigation. Practice panels do not hide their parent. */\n::ng-deep auth-anon-layout:has(vbango-room-game:not(.practice-mode)) { visibility: hidden; }\n\n* { box-sizing: border-box; }\n::selection { color: #123d70; background: #d4e5f8; }\nbutton { font: inherit; color: inherit; }\nbutton:focus-visible { outline: 2px solid var(--accent); outline-offset: 4px; }\n[hidden] { display: none !important; }\n.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }\n.chamber { position: relative; isolation: isolate; width: 100%; height: 100dvh; min-height: 440px; overflow: hidden; background: var(--canvas); }\n.game-header { position: relative; z-index: 20; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; height: 100px; width: min(1020px, 100%); margin: 0 auto; padding: 0 34px; }\n.brand { display: flex; align-items: center; gap: 10px; }\n.brand-mark { width: 28px; height: 28px; fill: none; stroke: var(--accent); stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }\n.brand-mark rect { fill: #e4edf8; }\n.brand h1 { margin: 0; font-size: 18px; font-weight: 500; letter-spacing: 2px; line-height: 1.3; }\n.chapter { display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 13px; font-variant-numeric: tabular-nums; line-height: 1; }\n.chapter-number { color: var(--accent); font-weight: 600; min-width: 20px; text-align: center; }\n.chapter-divider { color: #a6b2c0; }\n.chapter-total { color: var(--muted); }\n.reset { justify-self: end; display: grid; place-items: center; width: 44px; height: 44px; padding: 11px; border: 1px solid transparent; border-radius: 12px; color: #687b91; background: transparent; cursor: pointer; transition: background .16s ease, color .16s ease, border-color .16s ease; }\n.reset svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }\n.reset:hover { color: var(--accent); border-color: #d9e3ef; background: #edf2f8; }\n.reset:disabled { cursor: default; opacity: .45; }\n.scene { position: absolute; inset: 100px 0 76px; }\n.mechanism { position: absolute; z-index: 4; top: 48%; left: 50%; width: var(--board-size); aspect-ratio: 1; transform: translate(-50%, -50%); transition: opacity .38s ease, transform .7s cubic-bezier(.16,1,.3,1); }\n.board-plate { position: absolute; inset: -24px; border-radius: 22px; background: var(--paper); box-shadow: 0 13px 30px rgb(37 57 82 / 7%); }\n.board { position: absolute; inset: 0; display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(4, 1fr); gap: 8px; touch-action: none; user-select: none; -webkit-user-select: none; }\n.cell { position: relative; display: grid; place-items: center; min-width: 0; min-height: 0; padding: 0; border: 1px solid #e6ebf1; border-radius: var(--cell-radius); overflow: visible; background: #f0f3f7; cursor: pointer; -webkit-tap-highlight-color: transparent; transition: border-color .16s ease, background .16s ease, box-shadow .16s ease; }\n.cell::before { content: \"\"; position: absolute; width: 4px; height: 4px; border-radius: 50%; background: #c9d3df; pointer-events: none; }\n.cell:not(:disabled):hover { background: #e9eff6; border-color: #c9d9ec; }\n.cell:focus-visible { z-index: 6; outline-offset: 3px; }\n.cell.has-piece { border-width: 0; border-color: transparent; background: transparent; cursor: grab; }\n.cell.has-piece:active { cursor: grabbing; }\n.cell.is-selected { z-index: 5; outline: 2px solid var(--accent); outline-offset: 3px; }\n.cell.has-piece::before { display: none; }\n.cell:disabled { cursor: default; }\n.well { position: absolute; inset: 0; border: 1px solid color-mix(in srgb, var(--color) 40%, white); border-radius: var(--cell-radius); background: color-mix(in srgb, var(--color) 8%, white); pointer-events: none; }\n.well svg { position: absolute; top: 50%; left: 50%; width: 32%; height: 32%; transform: translate(-50%, -50%); fill: none; stroke: color-mix(in srgb, var(--color) 60%, #26384c); stroke-width: 1.4; stroke-linecap: round; stroke-linejoin: round; }\n.piece { --piece-ink: #ffffff; position: absolute; inset: 0; z-index: 2; display: grid; place-items: center; width: 100%; height: 100%; border: 1px solid color-mix(in srgb, var(--color) 88%, #29405b); border-radius: var(--cell-radius); background: var(--color); box-shadow: inset 0 1px 0 rgb(255 255 255 / 18%), 0 2px 3px rgb(37 57 82 / 9%); pointer-events: none; transition: opacity .16s ease, filter .16s ease, box-shadow .16s ease; }\n.piece[data-color=\"Y\"] { --piece-ink: #695014; }\n.piece svg { width: 35%; height: 35%; fill: none; stroke: var(--piece-ink); stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }\n.cell:not(:disabled):hover .piece { filter: brightness(1.04); }\n.cell.is-selected .piece { box-shadow: inset 0 1px 0 rgb(255 255 255 / 20%), 0 5px 10px rgb(37 57 82 / 13%); }\n.cell.is-drag-source .piece { opacity: .16; }\n.cell.is-correct .piece::after { content: \"\"; position: absolute; right: 9px; bottom: 9px; width: 5px; height: 5px; border-radius: 50%; background: var(--piece-ink); opacity: .75; }\n.cell.is-drop-target { border-color: var(--accent); background: #e4edf8; box-shadow: inset 0 0 0 1px var(--accent); }\n.cell.is-invalid { animation: knock .24s ease; }\n.drag-ghost { position: fixed; inset: auto; z-index: 50; height: auto; aspect-ratio: 1; margin: 0; opacity: .98; transform: translate(-50%, -50%); box-shadow: 0 12px 24px rgb(37 57 82 / 22%); }\n.plate-pin { position: absolute; width: 4px; height: 4px; border: 1px solid #c4cfdd; border-radius: 50%; }\n.pin-nw { top: -14px; left: -14px; }.pin-ne { top: -14px; right: -14px; }.pin-sw { bottom: -14px; left: -14px; }.pin-se { bottom: -14px; right: -14px; }\n.plate-indicator { position: absolute; left: 50%; bottom: -17px; width: 28px; height: 3px; transform: translateX(-50%); border-radius: 2px; background: #e3eaf3; overflow: hidden; }\n.plate-indicator span { display: block; width: 100%; height: 100%; background: var(--accent); transform: scaleX(.25); transition: transform .38s cubic-bezier(.16,1,.3,1); }\n.scene.is-advancing .plate-indicator span { transform: scaleX(1); }\n.scene.is-swapping .mechanism { opacity: 0; transform: translate(-50%, -48%) scale(.98); }\n.scene.is-rejected .mechanism { animation: plate-knock .25s ease; }\n\n/* The two precision leaves appear only when the mechanism is released. */\n.doorway { position: absolute; z-index: 2; top: 48%; left: 50%; width: calc(var(--board-size) + 76px); height: calc(var(--board-size) + 76px); transform: translate(-50%, -50%); border: 1px solid #d7e1ed; border-radius: 24px; overflow: hidden; opacity: 0; pointer-events: none; transition: opacity .24s ease; }\n.beyond-door { position: absolute; inset: 0; display: grid; place-items: center; background: #eef3f9; }\n.beyond-mark { width: 86px; height: 86px; fill: none; stroke: #759aca; stroke-width: 1; stroke-linecap: round; stroke-linejoin: round; }\n.beyond-mark rect { fill: #dce8f6; }\n.door-leaf { position: absolute; top: 0; bottom: 0; width: 50%; background: #fbfcfe; transition: transform 1.65s cubic-bezier(.65,0,.16,1) .16s; }\n.door-left { left: 0; border-right: 1px solid #c4d3e5; }\n.door-right { right: 0; border-left: 1px solid #c4d3e5; }\n.door-frame { position: absolute; inset: 14px; border: 1px solid #e2e9f2; border-radius: 12px; }\n.door-left .door-frame { right: 9px; border-radius: 12px 5px 5px 12px; }\n.door-right .door-frame { left: 9px; border-radius: 5px 12px 12px 5px; }\n.door-latch { position: absolute; top: calc(50% - 21px); width: 3px; height: 42px; border-radius: 2px; background: #83a5cc; }\n.door-left .door-latch { right: 14px; }.door-right .door-latch { left: 14px; }\n.door-seam { position: absolute; top: 9%; bottom: 9%; left: calc(50% - 1px); width: 2px; background: #4b82bf; transition: opacity .15s ease .3s; }\n.scene.is-revealing .doorway { opacity: 1; }\n.scene.is-revealing .door-left { transform: translateX(-100%); }\n.scene.is-revealing .door-right { transform: translateX(100%); }\n.scene.is-revealing .door-seam { opacity: 0; }\n.scene.is-revealing .mechanism { opacity: 0; transform: translate(-50%, -50%) scale(.94); pointer-events: none; }\n.scene.is-in-room .doorway { opacity: 0; }\n\n.completion { position: absolute; z-index: 8; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding-bottom: 24px; animation: settle .55s cubic-bezier(.16,1,.3,1) both; }\n.completion-sigil { width: 76px; height: 76px; margin-bottom: 24px; fill: none; stroke: var(--accent); stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }\n.completion h2 { margin: 0; color: var(--ink); font-size: 25px; font-weight: 500; letter-spacing: 2px; line-height: 1.5; }\n.scene.is-finished .mechanism { opacity: 0; transform: translate(-50%, -50%) scale(.98); pointer-events: none; }\n.inner-room { position: absolute; z-index: 9; inset: 0; display: grid; place-items: center; background: var(--canvas); animation: settle .45s cubic-bezier(.16,1,.3,1) both; }\n.room-sigil { width: 96px; height: 96px; fill: none; stroke: #7e9fc7; stroke-width: 1; stroke-linecap: round; stroke-linejoin: round; }\n.room-sigil rect { fill: #e0eafa; }\n.room-unlock { position: absolute; z-index: 13; top: 48%; left: 50%; width: min(430px, calc(100vw - 58px)); max-height: calc(100dvh - 180px); transform: translate(-50%, -50%); overflow: auto; overscroll-behavior: contain; }\n.room-unlock:empty { display: none; }\n\n@keyframes knock { 33% { transform: translateX(-3px); } 66% { transform: translateX(3px); } }\n@keyframes plate-knock { 33% { transform: translate(calc(-50% - 3px), -50%); } 66% { transform: translate(calc(-50% + 3px), -50%); } }\n@keyframes settle { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }\n\n@media (max-width: 600px) {\n  :host { --board-size: max(200px, min(340px, calc(100vw - 64px), calc(100dvh - 222px))); --cell-radius: 10px; }\n  .game-header { height: 84px; padding: 0 21px; }\n  .brand { gap: 8px; }.brand-mark { width: 25px; height: 25px; }.brand h1 { font-size: 17px; }\n  .chapter { gap: 8px; font-size: 12px; }\n  .scene { inset: 84px 0 64px; }\n  .mechanism, .doorway { top: 47%; }\n  .board { gap: 6px; }.board-plate { inset: -18px; border-radius: 18px; }\n  .piece svg, .well svg { width: 36%; height: 36%; }\n  .plate-pin { width: 3px; height: 3px; }\n  .pin-nw, .pin-ne { top: -11px; }.pin-sw, .pin-se { bottom: -11px; }.pin-nw, .pin-sw { left: -11px; }.pin-ne, .pin-se { right: -11px; }\n  .plate-indicator { bottom: -12px; width: 23px; height: 2px; }\n  .doorway { width: calc(var(--board-size) + 48px); height: calc(var(--board-size) + 48px); border-radius: 19px; }\n  .door-frame { inset: 9px; }.door-left .door-frame { right: 7px; }.door-right .door-frame { left: 7px; }\n  .door-left .door-latch { right: 10px; }.door-right .door-latch { left: 10px; }\n  .completion h2 { font-size: 23px; }\n}\n\n@media (max-height: 540px) {\n  :host { --board-size: max(216px, min(300px, calc(100vw - 64px), calc(100dvh - 140px))); }\n  .game-header { height: 62px; }.scene { top: 62px; bottom: 32px; }\n  .mechanism, .doorway { top: 48%; }\n}\n\n:host.practice-mode { position: relative; inset: auto; z-index: auto; width: 100%; height: 540px; overflow: hidden; --board-size: min(330px, calc(100vw - 100px)); }\n:host.practice-mode .chamber { height: 540px; min-height: 440px; }\n:host.practice-mode .game-header { height: 72px; padding-inline: 25px; }\n:host.practice-mode .scene { inset: 72px 0 45px; }\n:host.practice-mode .mechanism, :host.practice-mode .doorway { top: 49%; }\n\n@media (prefers-reduced-motion: reduce) {\n  *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; scroll-behavior: auto !important; }\n}\n"],
})
export class VbangoRoomGameComponent implements OnChanges, OnDestroy {
  @Input() busy = false;
  @Input() mode: "play" | "practice" = "play";
  @Output() readonly secretSubmitted = new EventEmitter<string>();
  // Only public entry intent; no secret route, credential, or authentication result.
  @Output() readonly roomEntry = new EventEmitter<void>();

  @ViewChild("chamber", { static: true }) private chamber!: ElementRef<HTMLElement>;
  @ViewChild("board", { static: true }) private board!: ElementRef<HTMLElement>;
  @ViewChildren("cell") private cellElements!: QueryList<ElementRef<HTMLButtonElement>>;

  readonly cells = Array.from({ length: 16 }, (_, index) => index);
  readonly stageIndices = [0, 1, 2];
  level = 0;
  phase: Phase = "playing";
  swapping = false;
  selected: Color | null = null;
  dragSource: number | null = null;
  hoverCell: number | null = null;
  invalidCell: number | null = null;
  statusText = "";
  rejecting = false;

  private positions: Arrangement = { ...START };
  private gesture: PointerGesture | null = null;
  private ghost: HTMLElement | null = null;
  private suppressedClickSource: number | null = null;
  private suppressedClickUntil = 0;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly frames = new Set<number>();
  private readonly animations = new Set<Animation>();
  private generation = 0;
  private destroyed = false;
  private entryEmitted = false;
  private pendingCredential = "";

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  @HostBinding("class.practice-mode")
  get practiceMode(): boolean { return this.mode === "practice"; }

  get canPlay(): boolean {
    return !this.destroyed && !this.busy && this.phase === "playing";
  }

  get chapterNumber(): string {
    return this.phase === "room" ? "◇" : ["I", "II", "III"][this.level];
  }

  get chapterLabel(): string {
    return this.phase === "room" ? "内室" : `第 ${this.level + 1} 关`;
  }

  colorAt(index: number): Color | null {
    return COLORS.find((color) => this.positions[color] === index) ?? null;
  }

  targetAt(index: number): Color | null {
    return COLORS.find((color) => LEVELS[this.level][color] === index) ?? null;
  }

  isCorrect(index: number): boolean {
    const color = this.colorAt(index);
    return color !== null && LEVELS[this.level][color] === index;
  }

  isSelected(index: number): boolean {
    const color = this.colorAt(index);
    return color !== null && color === this.selected;
  }

  colorValue(color: Color): string {
    return COLOR_VARIABLES[color];
  }

  glyphFor(color: Color): string {
    return GLYPHS[color];
  }

  cellLabel(index: number): string {
    const color = this.colorAt(index);
    const target = this.targetAt(index);
    const coordinate = `第 ${Math.floor(index / 4) + 1} 行第 ${index % 4 + 1} 列`;
    const content = color ? `${COLOR_NAMES[color]}色方块` : "空格";
    return `${coordinate}，${content}${target ? `，${COLOR_NAMES[target]}色归位槽` : ""}`;
  }

  clickCell(index: number, event: MouseEvent): void {
    const duplicatePointerClick = event.detail !== 0 &&
      this.suppressedClickSource === index && performance.now() < this.suppressedClickUntil;
    if (!duplicatePointerClick) this.activateCell(index);
  }

  keyCell(index: number, event: KeyboardEvent): void {
    if (!this.canPlay) return;
    let next = index;
    switch (event.key) {
      case "ArrowLeft": if (index % 4 > 0) next -= 1; break;
      case "ArrowRight": if (index % 4 < 3) next += 1; break;
      case "ArrowUp": if (index >= 4) next -= 4; break;
      case "ArrowDown": if (index < 12) next += 4; break;
      case "Escape":
        this.selected = null;
        this.releaseDrag();
        event.preventDefault();
        return;
      default: return;
    }
    event.preventDefault();
    this.cellElements.get(next)?.nativeElement.focus();
  }

  pointerDown(event: PointerEvent): void {
    if (!this.canPlay || this.gesture || !event.isPrimary || event.button !== 0) return;
    const cell = event.target instanceof Element ? event.target.closest<HTMLButtonElement>(".cell") : null;
    if (!cell || !this.board.nativeElement.contains(cell)) return;
    const source = Number(cell.dataset["index"]);
    const color = this.colorAt(source);
    if (!color) return;
    this.gesture = { pointerId: event.pointerId, color, source, x: event.clientX, y: event.clientY, moved: false };
    this.board.nativeElement.setPointerCapture(event.pointerId);
  }

  pointerMove(event: PointerEvent): void {
    if (!this.gesture || this.gesture.pointerId !== event.pointerId) return;
    if (!this.canPlay) { this.releaseDrag(); return; }
    if (!this.gesture.moved && Math.hypot(event.clientX - this.gesture.x, event.clientY - this.gesture.y) >= 6) {
      const original = this.cellElements.get(this.gesture.source)?.nativeElement.querySelector<HTMLElement>(".piece");
      if (!original) { this.releaseDrag(); return; }
      this.gesture.moved = true;
      this.ghost = original.cloneNode(true) as HTMLElement;
      this.ghost.classList.add("drag-ghost");
      this.ghost.setAttribute("aria-hidden", "true");
      this.ghost.style.width = `${original.getBoundingClientRect().width}px`;
      this.chamber.nativeElement.append(this.ghost);
      this.dragSource = this.gesture.source;
    }
    if (!this.gesture.moved || !this.ghost) return;
    event.preventDefault();
    this.ghost.style.left = `${event.clientX}px`;
    this.ghost.style.top = `${event.clientY}px`;
    const target = this.cellUnderPointer(event);
    this.hoverCell = target !== null && !this.colorAt(target) ? target : null;
  }

  pointerUp(event: PointerEvent): void {
    if (!this.gesture || this.gesture.pointerId !== event.pointerId) return;
    const current = { ...this.gesture };
    const destination = this.cellUnderPointer(event);
    this.releaseDrag();
    this.suppressedClickSource = current.source;
    this.suppressedClickUntil = performance.now() + 180;
    if (!current.moved) this.activateCell(current.source);
    else if (destination !== null) this.movePiece(current.color, destination);
  }

  pointerCancel(): void {
    this.releaseDrag();
  }

  lostPointerCapture(): void {
    if (this.gesture) this.releaseDrag();
  }

  @HostListener("window:blur")
  windowBlur(): void {
    this.releaseDrag();
  }

  reset(): void {
    if (this.busy || this.destroyed) return;
    this.resetState();
  }

  rejectAttempt(): void {
    if (this.destroyed) return;
    this.resetState();
    this.rejecting = true;
    this.statusText = "机关没有闭合，请复位后再试一次。";
    this.schedule(() => { this.rejecting = false; }, 280);
    this.changeDetector.markForCheck();
  }

  private resetState(): void {
    this.cancelScheduled();
    this.releaseDrag();
    this.positions = { ...START };
    this.level = 0;
    this.phase = "playing";
    this.swapping = false;
    this.selected = null;
    this.invalidCell = null;
    this.entryEmitted = false;
    this.pendingCredential = "";
    this.rejecting = false;
    this.suppressedClickSource = null;
    this.suppressedClickUntil = 0;
    this.statusText = "机关已复位。";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["mode"] && !changes["mode"].firstChange) this.resetState();
    if (changes["busy"] && this.busy) this.releaseDrag();
    else if (changes["busy"] && this.phase === "room") this.emitEntryIntent();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelScheduled();
    this.releaseDrag();
    this.pendingCredential = "";
    this.secretSubmitted.complete();
    this.roomEntry.complete();
  }

  private activateCell(index: number): void {
    if (!this.canPlay || !this.validCell(index)) return;
    const occupant = this.colorAt(index);
    if (occupant) {
      this.selected = this.selected === occupant ? null : occupant;
      this.statusText = this.selected ? `已选中${COLOR_NAMES[this.selected]}色方块。` : "方块已放下。";
    } else if (this.selected) {
      this.movePiece(this.selected, index);
    }
  }

  private movePiece(color: Color, destination: number): boolean {
    if (!this.canPlay || !this.validCell(destination) || !COLORS.includes(color)) return false;
    if (this.positions[color] === destination) return false;
    if (this.colorAt(destination)) {
      this.invalidCell = destination;
      this.statusText = "这个方块尚不能落在这里。";
      this.schedule(() => { this.invalidCell = null; }, 260);
      return false;
    }
    const sourceRect = this.cellElements.get(this.positions[color])?.nativeElement.getBoundingClientRect();
    this.positions = { ...this.positions, [color]: destination };
    this.selected = null;
    this.invalidCell = null;
    this.statusText = `${COLOR_NAMES[color]}色方块移至第 ${Math.floor(destination / 4) + 1} 行第 ${destination % 4 + 1} 列。`;
    if (sourceRect && !this.reducedMotion()) {
      const ownGeneration = this.generation;
      const frame = requestAnimationFrame(() => {
        this.frames.delete(frame);
        if (this.destroyed || ownGeneration !== this.generation) return;
        const destinationCell = this.cellElements.get(destination)?.nativeElement;
        const piece = destinationCell?.querySelector<HTMLElement>(".piece");
        if (!piece || !destinationCell) return;
        const targetRect = destinationCell.getBoundingClientRect();
        const animation = piece.animate([
          { transform: `translate(${sourceRect.left - targetRect.left}px, ${sourceRect.top - targetRect.top}px) scale(1.02)` },
          { transform: "translate(0, 0) scale(1)" },
        ], { duration: 220, easing: "cubic-bezier(.2,.6,.2,1)" });
        this.animations.add(animation);
        animation.addEventListener("finish", () => this.animations.delete(animation), { once: true });
      });
      this.frames.add(frame);
    }
    this.inspectArrangement();
    return true;
  }

  private inspectArrangement(): void {
    if (!this.canPlay) return;
    if (this.mode === "play" && this.matches(LEVELS[this.level])) this.finishLevel();
    else if (this.level === 0 && COLORS.every((color) => [0, 3, 12, 15].includes(this.positions[color]))) this.openRoom();
  }

  private finishLevel(): void {
    this.pendingCredential = "";
    this.phase = "advancing";
    this.selected = null;
    this.releaseDrag();
    if (this.level === LEVELS.length - 1) {
      this.schedule(() => {
        this.phase = "finished";
        this.statusText = "恭喜过关。三重机关已全部归位。";
      }, 1000);
      return;
    }
    this.schedule(() => { this.swapping = true; }, 650);
    this.schedule(() => {
      this.level += 1;
      this.positions = { ...START };
      this.swapping = false;
      this.phase = "playing";
      this.statusText = `第 ${this.level + 1} 关。`;
    }, 1250);
  }

  private openRoom(): void {
    let credential = "";
    try { credential = encodeArrangement({ ...this.positions }); } catch { this.rejectAttempt(); return; }
    if (!isRoomRouteCredential(credential)) { this.rejectAttempt(); return; }
    this.pendingCredential = credential;
    this.phase = "revealing";
    this.selected = null;
    this.releaseDrag();
    this.statusText = "机关门正在开启。";
    this.schedule(() => {
      this.phase = "room";
      this.statusText = "已进入内室。";
      this.emitEntryIntent();
    }, 2200);
  }

  private emitEntryIntent(): void {
    if (this.destroyed || this.busy || this.phase !== "room" || this.entryEmitted) return;
    if (!isRoomRouteCredential(this.pendingCredential)) { this.rejectAttempt(); return; }
    this.entryEmitted = true;
    const credential = this.pendingCredential;
    this.pendingCredential = "";
    this.secretSubmitted.emit(credential);
    if (!this.destroyed && this.phase === "room" && this.entryEmitted) this.roomEntry.emit();
  }

  private validCell(index: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < 16;
  }

  private matches(arrangement: Arrangement): boolean {
    return COLORS.every((color) => this.positions[color] === arrangement[color]);
  }

  private cellUnderPointer(event: PointerEvent): number | null {
    const element = this.board.nativeElement.ownerDocument.elementFromPoint(event.clientX, event.clientY);
    const cell = element?.closest<HTMLButtonElement>(".cell");
    if (!cell || !this.board.nativeElement.contains(cell)) return null;
    const index = Number(cell.dataset["index"]);
    return this.validCell(index) ? index : null;
  }

  private releaseDrag(): void {
    const current = this.gesture;
    this.gesture = null;
    if (current && this.board?.nativeElement.hasPointerCapture(current.pointerId)) {
      this.board.nativeElement.releasePointerCapture(current.pointerId);
    }
    this.ghost?.remove();
    this.ghost = null;
    this.dragSource = null;
    this.hoverCell = null;
  }

  private reducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private schedule(callback: () => void, delay: number): void {
    const ownGeneration = this.generation;
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (this.destroyed || ownGeneration !== this.generation) return;
      callback();
      if (!this.destroyed) this.changeDetector.markForCheck();
    }, this.reducedMotion() ? Math.min(delay, 80) : delay);
    this.timers.add(timer);
  }

  private cancelScheduled(): void {
    this.generation += 1;
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.frames.forEach((frame) => cancelAnimationFrame(frame));
    this.frames.clear();
    this.animations.forEach((animation) => animation.cancel());
    this.animations.clear();
  }
}
